import {
  max,
  mean,
  merge,
} from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';

import { getTimeFragment } from './NutritionSettingsContext';
import { idKey, timeSort } from '../utils/dataUtils';
import { getHourBasedTimeScale, getSlowTransition, remove } from '../utils/svgUtils';
import './Dashboard.css';

const measures = {
  chartWidth: 720,
  chartHeight: 350,
};

const y = scaleLinear().range([measures.chartHeight, measures.chartHeight / 8]);

const getCarbs = item => item.carbohydrate;

const getY = item => y(getCarbs(item));

function render(selection) {
  const {
    data,
    // days,
    locale,
    mealThreshold,
    mealTimes,
    selectedMealTime,
    selectMeal,
    selectMealTime,
    // t,
    width = 720,
  } = selection.datum();

  // Keep the previus data set hidden here so we can compare...
  const existingData = selection.selectAll('line.snack.limit').data();

  if (existingData &&
    existingData[0] &&
    existingData[0].data === data &&
    existingData.mealTimes &&
    !existingData.mealTimes.some((t, i) => t.start !== mealTimes[i].start || t.end !== mealTimes[i].end) &&
    existingData[0].locale === locale) {
    // Data has not changed, let's not re-render
    return;
  }

  const canvas = selection;

  canvas.selectAll('g.entries').data(['meal', 'snack'])
  .join('g')
  .attr('id', d => `${d}EntriesG`)
  .attr('class', d=> `${d} entries`);

  const nutrition = merge(Object.values(data || {}))
    .filter(d => !!d.carbohydrate)
    .sort(timeSort);

  const timeScale = getHourBasedTimeScale({ width });
  const slowTransition = getSlowTransition();

  const maxCarbs = max(nutrition, getCarbs) || 0;
  y.domain([0, maxCarbs + 5]);
  // y.domain([0, 155]);

  function getMealTime(entry, time = new Date(entry.time)) {
    const fragment = getTimeFragment(time);
    let mealTime = 'snack';
    Object.values(mealTimes).some((t, i) => {
      const { start, end } = t;
      if ((entry.carbohydrate >= mealThreshold)
        && (fragment >= start)
        && (fragment <= end)) {
        mealTime = Object.keys(mealTimes)[i];
        return true;
      }
      return false;
    });
    if (!mealTime) {
      console.error('Did not find meal time for meal', entry, mealTimes);
    }
    return mealTime;
  }

  const mealValues = nutrition.reduce((o, n) => {
    const time = new Date(n.time);
    const mealTime = getMealTime(n, time);
    if (!o[mealTime]) {
      o[mealTime] = {
        name: mealTime,
        max: 0,
        average: 0,
        entries: [],
        values: [],
        start: -1,
        end: 0,
      }
    }
    o[mealTime].max = Math.max((o[mealTime] || {}).max, n.carbohydrate);
    const coord = timeScale({ time: time.toISOString() });
    o[mealTime].start = o[mealTime].start === -1 ? coord : Math.min(o[mealTime].start, coord);
    o[mealTime].end = Math.max(o[mealTime].end, coord);
    o[mealTime].entries.push(n);
    return o;
  }, Object.keys(mealTimes).reduce((m, t) => {
    m[t] = {
      name: t,
      max: 0,
      average: 0,
      entries: [],
      values: [],
      start: -1,
      end: 0,
    }
    return m;
  }, {}));

  Object.values(mealValues).forEach((v) => {
    v.average = mean(v.entries.map(e => e.carbohydrate).sort()) || 0;
  });

  const snackLimit = y(mealThreshold - 1);

  const { snack, ...meals } = mealValues;

  // const filteredMeals = Object.values({ ...meals }).filter(m => m.entries.length > 3); // Make relative to days!
  const filteredMeals = Object.values({ ...meals });

  const entriesG = canvas.selectAll('g.meal.entries');

  const mealTimeG = entriesG.selectAll('g.mealtime')
  .data(filteredMeals, d => d.name);

  mealTimeG.exit().transition(slowTransition)
    .style('opacity', 0)
    .on('end interrupt', remove);

  const mealTimeGEnter = mealTimeG.enter().append('g')
    .attr('class', d => `meal${d.name} mealtime`);
  mealTimeGEnter.append('rect')
    .attr('class', d => `meal${d.name} meal carbs${d.name === selectedMealTime ? ' selected' : ''}`)
    .attr('rx', 5)
    .attr('x', d => d.start - 5)
    .attr('y', snackLimit)
    .attr('width', d => d.end - d.start + 10)
    .attr('height', 5)

  mealTimeGEnter.append('line')
    .attr('class', 'carb')
    .attr('x1', d => d.start - 5)
    .attr('x2', d => d.end + 5)
    .attr('y1', d => y(0))
    .attr('y2', d => y(0));

  mealTimeGEnter.append('text')
    .attr('class', 'carb')
    .attr('y', y(0) - 15)
    .attr('x', d => d.start + ((d.end - d.start) / 2))
    .style('opacity', 0)
    .text(d => d.average ? `~${d.average.toFixed(0)} g` : '');

  const update = mealTimeGEnter.merge(mealTimeG)
    .classed('selected', (d, i) => i === selectedMealTime);

  update.select('rect.carbs')
    .classed('selected', (d, i) => i === selectedMealTime);

  const snackHeight =  y(0) - snackLimit;
  const mealMask = canvas.selectAll('#mealMask').data([{ data, locale }]);

  const carbRects = canvas.selectAll('rect.carbs');
  carbRects.on('click.selectMealTime', function (event, d) {
    const nodes = carbRects.nodes();
    const i = nodes.indexOf(this);
    const me = select(this);
    if (me.classed('selected')) {
      me.classed('selected', false);
      selectMealTime(undefined);
      return;
    }
    canvas.selectAll('rect.carbs').classed('selected', (rd) => rd === d);
    selectMealTime(i);
  });

  const mealMaskEnter = mealMask.enter().insert('mask', '#snackEntriesG')
    .attr('id', 'mealMask');
  
  const mealData = mealTimeGEnter.data();

  mealMaskEnter.append('rect')
    .attr('class', 'visible')
    .attr('width', width)
    .style('fill', 'white');
  mealMask.selectAll('.hide').data(mealData)
  .join('rect')
    .attr('class', 'hide')
    .attr('rx', 5)
    .attr('x', d => d.start - 5)
    .attr('y', d => y(d.max) - 5)
    .attr('width', d => d.end - d.start + 10)
    .attr('height', d => snackLimit - y(d.max) + 10)
    .style('fill', 'black')
    .style('fill-opacity', '100%');

  mealMask.merge(mealMaskEnter).selectAll('rect.visible')
    .attr('y', snackLimit)
    .attr('height', snackHeight);
  
  const snackG = canvas.selectAll('g.snack.entries').data([{ data, locale }])
  .join('g')
    .attr('mask', 'url(#mealMask');

  snackG.selectAll('rect.snack').data([mealThreshold])
  .join('rect')
    .attr('class', 'snack carbs')
    .attr('width', width)
    .attr('y', snackLimit)
    .attr('height', snackHeight);
  snackG.selectAll('line.snack.limit').data([{ data, locale, mealTimes }])
    .join('line')
      .attr('class', 'snack limit')
      .attr('x2', width)
      .attr('y1', snackLimit)
      .attr('y2', snackLimit);
    
  const dots = canvas.selectAll('circle.carb.entry').data(nutrition, idKey);
  dots.exit()
    .style('fill-opacity', 1)
    .transition(slowTransition)
    .attr('r', 0)
    .style('fill-opacity', 0)
    .on('end', remove);

  const dotsEnter = dots.enter().append('circle')
    .attr('class', 'carb entry')
    .attr('class', d => `${getMealTime(d)} carb entry`)
    .attr('r', 0)
    .style('fill-opacity', 0);
  dotsEnter.append('title')
    .text(d => JSON.stringify(d, null, 2));
  dotsEnter.merge(dots)
    .attr('cx', timeScale)
    .attr('cy', getY)
    .transition(slowTransition)
    .attr('r', 3)
    .style('fill-opacity', 1)
    .on('end', function () {
      select(this).style('fill-opacity', null);
    });

  update.selectAll('circle.carb.entry')
    .on('click.selectMeal', function (event, d) {
      const me = select(this);
      if (me.classed('selected')) {
        me.classed('selected', false);
        selectMeal(undefined);
        return;
      }
      canvas.selectAll('rect.carbs').classed('selected', (rd) => rd === d);
      selectMeal(d);
    });
  

  const updateT = update.transition(slowTransition);

  const exiting = updateT.filter(d => d.start < 0);
  const updating = updateT.filter(d => d.start >= 0);

  exiting.select('rect.carbs')
    .attr('y', snackLimit)
    .attr('x', function() {
      const me = select(this);
      const newX = +me.attr('x') + (+me.attr('width') / 2 );
      return newX;
    })
    .attr('width', 0)
    .attr('height', 0);

  exiting.select('line.carb')
    .attr('y1', snackLimit)
    .attr('y2', snackLimit)
    .style('opacity', 0);

  exiting.select('text')
    .attr('y', snackLimit - 14)
    .style('opacity', 0)
    .on('end', function (d) {
      select(this).text('');
    });

  updating.select('rect.carbs')
    .attr('x', d => d.start - 5)
    .attr('y', d => y(d.max) - 5)
    .attr('width', d => d.end - d.start + 10)
    .attr('height', d => snackLimit - y(d.max) + 10);

  updating.select('line.carb')
    .attr('x1', d => d.start - 5)
    .attr('x2', d => d.end + 5)
    .attr('y1', d => y(d.average))
    .attr('y2', d => y(d.average))
    .style('opacity', null);

  updating.select('text')
    .attr('y', d => y(d.max) - 15)
    .attr('x', d => d.start + ((d.end - d.start) / 2))
    .style('opacity', null)
    .text(d => d.average ? `~${d.average.toFixed(0)} g` : '');
};

const DailyNutritionGraph = {
  init: f => f,
  render,
  destroy: f => f,
};

export default DailyNutritionGraph;
